import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import Checkbox from '@/components/atoms/Checkbox';
import SplitValueElement from '@/components/atoms/SplitValueElement';
import Tooltip from '@/components/atoms/Tooltip';
import type { DataTableColumnDef } from '@/components/organisms/DataTable';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';
import { createCageUrl } from '@/utils/urlGenerator';

import { AvgTotalCell, AvgTotalSummaryCell } from './AvgTotalCell';
import CountCell from './CountCell';
import type { LiceReportLocationTableProps, LocationCage } from './LiceReportLocationTable';

import styles from './LiceReportLocationTable.module.scss';

function StageHeader(props: { label: string }) {
    const { t } = useTranslation();
    return (
        <span>
            <span>{props.label}</span>
            <SplitValueElement>
                <span>{t('organisms.liceReportLocationTable.average')}</span>
                <span>{t('common.labels.total')}</span>
            </SplitValueElement>
        </span>
    );
}

function PlainHeader(props: { label: string }) {
    return (
        <span>
            <span>{props.label}</span>
        </span>
    );
}

function FooterFigure(props: { tooltip: string; children: string }) {
    return (
        <Tooltip content={props.tooltip} layout="fit" targetClassName={styles.footerTooltip}>
            <em>{props.children}</em>
        </Tooltip>
    );
}

type ColumnsInput = Pick<
    LiceReportLocationTableProps,
    'reportIndex' | 'clientId' | 'isChileanLice' | 'locationId' | 'averages' | 'mode'
> & {
    onHandleCageSelected: LiceReportLocationTableProps['onHandleCageSelected'];
};

/**
 * The report's columns: open link, pen, the three lice stages as average and total, count,
 * weight, depth and the include toggle. The location's averages are the footer row.
 */
export function useLiceReportColumns(input: ColumnsInput): DataTableColumnDef<LocationCage>[] {
    const { reportIndex, clientId, isChileanLice, locationId, averages, mode = 'weekly' } = input;
    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);
    const weight = (grams: number) => formatNumberByLocale(grams, locale, 0);

    const stages: DataTableColumnDef<LocationCage>[] = [
        {
            id: 'female',
            header: () => (
                <StageHeader
                    label={t(
                        isChileanLice
                            ? 'common.labels.ovigerousFemale'
                            : 'organisms.liceReportLocationTable.adultFemale'
                    )}
                />
            ),
            cell: ({ row }) => (
                <AvgTotalCell average={row.original.femaleAvg} total={row.original.femaleTotal} />
            ),
            footer: () => (
                <AvgTotalSummaryCell
                    average={averages.femaleAvg}
                    total={averages.femaleTotal}
                    averageTooltip={t('organisms.liceReportLocationTable.averageAdultFemale')}
                    totalTooltip={t('common.labels.totalAdultFemale')}
                />
            ),
        },
        {
            id: 'mobile',
            header: () => <StageHeader label={t('common.labels.mobileLice')} />,
            cell: ({ row }) => (
                <AvgTotalCell
                    average={row.original.movingStageAvg}
                    total={row.original.movingStageTotal}
                />
            ),
            footer: () => (
                <AvgTotalSummaryCell
                    average={averages.movingStageAvg}
                    total={averages.movingStageTotal}
                    averageTooltip={t('organisms.liceReportLocationTable.averageMobile')}
                    totalTooltip={t('common.labels.totalMobile')}
                />
            ),
        },
    ];
    if (!isChileanLice) {
        stages.push({
            id: 'caligus',
            header: () => <StageHeader label={t('common.labels.caligusElongatus')} />,
            cell: ({ row }) => (
                <AvgTotalCell
                    average={row.original.caligusElongatusAvg}
                    total={row.original.caligusElongatusTotal}
                />
            ),
            footer: () => (
                <AvgTotalSummaryCell
                    average={averages.caligusElongatusAvg}
                    total={averages.caligusElongatusTotal}
                    averageTooltip={t('organisms.liceReportLocationTable.averageCaligus')}
                    totalTooltip={t('common.labels.totalCaligus')}
                />
            ),
        });
    }

    return [
        {
            id: 'open',
            header: '',
            meta: {
                headerScreenReaderText: t('common.labels.cage'),
                cellTitle: (row) =>
                    t('liceReport.table.openCageLiceCount', { name: row.original.name }),
            },
            cell: ({ row }) => (
                <Button
                    to={`${createCageUrl(clientId, locationId, row.original.cageId)}/lice`}
                    variant="table"
                    tooltip={t('liceReport.table.openCageLiceCount', { name: row.original.name })}
                    size="small">
                    <span>{t('organisms.common.open')}</span>
                </Button>
            ),
            footer: '',
        },
        {
            id: 'name',
            header: () => <PlainHeader label={t('common.labels.name')} />,
            cell: ({ row }) => <span>{row.original.name}</span>,
            footer: '',
        },
        ...stages,
        {
            id: 'count',
            header: () => <PlainHeader label={t('common.labels.count')} />,
            cell: ({ row }) => <CountCell count={row.original.count} mode={mode} />,
            footer: () => (
                <FooterFigure tooltip={t('organisms.liceReportLocationTable.totalCount')}>
                    {formatNumberByLocale(averages.count, locale, 0)}
                </FooterFigure>
            ),
        },
        {
            id: 'weight',
            header: () => <PlainHeader label={t('organisms.liceReportLocationTable.weightG')} />,
            cell: ({ row }) => <span>{weight(row.original.livingWeight)}</span>,
            footer: () => (
                <FooterFigure tooltip={t('organisms.liceReportLocationTable.averageWeight')}>
                    {weight(averages.livingWeight)}
                </FooterFigure>
            ),
        },
        {
            id: 'depth',
            header: () => <PlainHeader label={t('organisms.liceReportLocationTable.depthM')} />,
            cell: ({ row }) => (
                <span>{formatNumberByLocale(row.original.depthAvg, locale, 2)}</span>
            ),
            footer: () => (
                <FooterFigure tooltip={t('organisms.liceReportLocationTable.averageDepth')}>
                    {formatNumberByLocale(averages.depthAvg, locale, 2)}
                </FooterFigure>
            ),
        },
        {
            id: 'include',
            header: () => <PlainHeader label={t('organisms.liceReportLocationTable.include')} />,
            cell: ({ row }) => (
                <Checkbox
                    aria-label={t('organisms.liceReportLocationTable.includeCage', {
                        name: row.original.name,
                    })}
                    checked={row.original.isIncluded ?? false}
                    onCheckedChange={() =>
                        input.onHandleCageSelected(reportIndex, locationId, row.original)
                    }
                />
            ),
            footer: '',
        },
    ];
}
